import * as Yup from 'yup';
import {
  parsePhoneNumberWithError,
  ParseError,
  validatePhoneNumberLength,
  PhoneNumber,
} from 'libphonenumber-js/max';

import { get, isEmpty } from 'lodash';
import { StringHelper } from '../helpers/string-helper';
import { ValidationHelper } from '../helpers';
import { OffenderIdentifierType, provinceValues } from '../enums';
import { IdentifierEntry } from './types';

function matchPasswords() {
  return this.parent.password === this.parent.confirmPassword;
}

function entityArrayLengthOfOne() {
  return this.parent.businessEntities.length >= 1;
}

function linkArrayLengthOfOne() {
  return this.parent.links.length >= 1;
}

function matchEmail() {
  return this.parent.email === this.parent.confirmEmail;
}

function matchIdNumber() {
  return this.parent.idNumber === this.parent.confirmIdNumber;
}

const errorMessage = (err) => {
  switch (err) {
    case 'NOT_A_NUMBER':
      return 'This number contains invalid characters';
    case 'INVALID_COUNTRY':
      return 'The country code is invalid';
    case 'TOO_SHORT':
      return 'This number is too short';
    case 'INVALID_LENGTH':
      return 'This number has an invalid length';
    case 'TOO_LONG':
      return 'This number is too long';
    default:
      return 'There is an error with the number entered';
  }
};

const normaliseIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : '';

function collectIdentifierEntries(root: unknown): IdentifierEntry[] {
  const mainAccount: IdentifierEntry[] = [];
  const familyMembers: IdentifierEntry[] = [];
  const businessIdentifiers: IdentifierEntry[] = [];

  const walk = (node: unknown, path: string) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;

    Object.entries(node).forEach(([key, value]) => {
      const childPath = path ? `${path}.${key}` : key;

      if (key === 'idNumber') {
        const normalised = normaliseIdentifier(value);
        if (normalised) {
          const target = path.includes('familyMembers') ? familyMembers : mainAccount;
          target.push({ path: childPath, value: normalised });
        }
        return;
      }

      if (key === 'familyMembers' && Array.isArray(value)) {
        value.forEach((member, index) => walk(member, `${childPath}[${index}]`));
        return;
      }

      if (key === 'businessEntities' && Array.isArray(value)) {
        value.forEach((businessEntity, entityIndex) =>
          walk(businessEntity, `${childPath}[${entityIndex}]`),
        );
        return;
      }

      if (key === 'links' && Array.isArray(value)) {
        value.forEach((link, linkIndex) => {
          const normalised = normaliseIdentifier(get(link, 'value'));
          if (normalised) {
            businessIdentifiers.push({
              path: `${childPath}[${linkIndex}].value`,
              value: normalised,
            });
          }
        });
        return;
      }

      walk(value, childPath);
    });
  };

  walk(root, '');

  return [...mainAccount, ...familyMembers, ...businessIdentifiers];
}

function duplicateIdentifierMessage(earlierPath: string): string {
  if (earlierPath.includes('familyMembers')) {
    return 'This ID number has already been added for another family member';
  }
  if (earlierPath.includes('businessEntities')) {
    return 'This identifier has already been added to a business entity';
  }
  return 'This ID number is already the main account ID number';
}

function uniqueIdentifierIn(field: string) {
  return function uniqueIdentifier(this: Yup.TestContext) {
    const fieldPath = `${this.path}.${field}`;
    const entries = collectIdentifierEntries(this.options.context ?? {});

    const position = entries.findIndex((entry) => entry.path === fieldPath);
    if (position < 0) return true;

    const earlier = entries
      .slice(0, position)
      .find((entry) => entry.value === entries[position].value);
    if (!earlier) return true;

    return this.createError({
      path: fieldPath,
      message: duplicateIdentifierMessage(earlier.path),
    });
  };
}

const baseOffenderValidationSchema = {
  offenderIdentifierIndividualType: Yup.number().oneOf(
    [OffenderIdentifierType.SaIdNumber, OffenderIdentifierType.Trn],
    'link must be one of ID or TRN',
  ),
  offenderIdentifierFleetType: Yup.number().oneOf(
    [OffenderIdentifierType.ProxyId, OffenderIdentifierType.Brn, OffenderIdentifierType.SaIdNumber],
    'link must be one of Proxy ID, BRN, or ID',
  ),
  offenderIdentifierIndividualValue: Yup.string().when('identifierType', (identifierType) =>
    identifierType?.toString() === OffenderIdentifierType.SaIdNumber.toString()
      ? Yup.string()
          .length(13, 'ID Number must be 13 digits long')
          .test(
            'containsInvalidCharacter',
            'Identifier can only contain letter or number',
            StringHelper.isLettersOrDigits,
          )
          .test('isInvalidIdNumber', 'Identifier is not valid', ValidationHelper.IsLuhnValid)
          .required('ID Number is Required')
      : Yup.string()
          .test(
            'isInvalidTrn',
            'TRN needs to be between 10-13 characters',
            (value: string | undefined) => !(value.length < 10 || value.length > 13),
          )
          .test(
            'containsInvalidCharacter',
            'Identifier can only contain letter or number',
            StringHelper.isLettersOrDigits,
          )
          .required('ID/TRN Number is Required'),
  ),
  offenderIdentifierFleetValue: Yup.string().when('identifierType', (identifierType) => {
    if (identifierType?.toString() === OffenderIdentifierType.ProxyId.toString()) {
      return Yup.string()
        .length(13, 'Proxy ID must be 13 digits long')
        .test(
          'containsInvalidCharacter',
          'Identifier can only contain letter or number',
          StringHelper.isLettersOrDigits,
        )
        .test('isInvalidIdNumber', 'Identifier is not valid', ValidationHelper.IsLuhnValid)
        .required('Proxy ID is Required');
    }
    if (identifierType?.toString() === OffenderIdentifierType.SaIdNumber.toString()) {
      return Yup.string()
        .length(13, 'ID Number must be 13 digits long')
        .test(
          'containsInvalidCharacter',
          'Identifier can only contain letter or number',
          StringHelper.isLettersOrDigits,
        )
        .test('isInvalidIdNumber', 'Identifier is not valid', ValidationHelper.IsLuhnValid)
        .required('ID Number is Required');
    }
    return Yup.string()
      .length(13, 'BRN must be 13 digits long')
      .test(
        'containsInvalidCharacter',
        'Identifier can only contain letter or number',
        StringHelper.isLettersOrDigits,
      )
      .required('BRN Number is Required');
  }),
};

const baseLinksValidationSchema = {
  links: Yup.array()
    .of(
      Yup.lazy((value) => {
        if (!get(value, 'identifierType', '') && !get(value, 'value', '')) {
          return Yup.mixed().notRequired();
        }
        return Yup.object()
          .shape({
            identifierType: Yup.number().oneOf(
              [
                OffenderIdentifierType.ProxyId,
                OffenderIdentifierType.Brn,
                OffenderIdentifierType.SaIdNumber,
              ],
              'link must be one of proxy ID, BRN, or ID',
            ),
            value: baseOffenderValidationSchema.offenderIdentifierFleetValue,
          })
          .test('uniqueLinkIdentifier', '', uniqueIdentifierIn('value'));
      }) as any,
    )
    .max(20, 'You can only add up to 20 BRN/Proxy.'),
};

const validationSchema = {
  email: Yup.string().email('Email must be a valid email').required('Email is required'),
  confirmEmail: Yup.mixed()
    .test('match', 'Email does not match', matchEmail)
    .required('Re-enter your email'),
  password: Yup.string()
    .min(6, 'Password must contain at least 6 characters')
    .required('Enter your password'),
  confirmPassword: Yup.mixed()
    .test('match', 'Password does not match', matchPasswords)
    .required('Re-enter your password'),
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  code: Yup.string()
    .matches(/^[^a-zA-Z!@#$%^&*(),.?":{}|<>+-]/, 'Must only contain numbers')
    .required('International code is required'),
  cellphone: Yup.string().test({
    // fieldName: 'cellphone',
    test: function internationalPhoneTest() {
      const intCode = this.parent.code;
      const cellNum = this.parent.cellphone;
      const internationalNumber = `+${intCode} ${cellNum}`;
      let phoneError;
      let phoneNumber;
      try {
        phoneNumber = parsePhoneNumberWithError(internationalNumber);
        // isValid() is returning false with the 3 digit intCode. Work around is to specifically test those cases
        phoneError =
          intCode.length === 3 && cellNum.length === 9
            ? 'Success'
            : phoneNumber.isValid()
            ? 'Success'
            : 'Invalid';
      } catch (error) {
        if (error instanceof ParseError) {
          phoneError = error.message;
        } else {
          phoneError = error;
        }
      }

      if (phoneError === 'Success') {
        return true;
      }
      if (phoneError === 'Invalid') {
        return this.createError({
          message: `${errorMessage(validatePhoneNumberLength(internationalNumber))}`,
          path: 'cellphone',
        });
      }
      return this.createError({
        message: `${errorMessage(phoneError)}`,
        path: 'cellphone',
      });
    },
  }),
  cellphoneNumber: Yup.string(),
  phoneNumber: Yup.string().required('Number is required').min(10),
  otp: Yup.string().required('OTP required').length(7),
  termsChecked: Yup.boolean().oneOf([true], 'Terms must be accepted'),
  province: Yup.mixed()
    .oneOf(provinceValues, 'Province is required')
    .required('Province is required'),
  idNumber: baseOffenderValidationSchema.offenderIdentifierIndividualValue,
  confirmIdNumber: Yup.string()
    .required('Re-enter your Identifier')
    .test('match', 'ID number does not match', matchIdNumber),
  communicationEmail: Yup.string()
    .email('Email must be a valid email')
    .test(
      'unique-emails',
      'Cannot add main account email as additional communication email',
      function validateCommunicationEmail(value) {
        const { email } = this.parent;
        return value !== email;
      },
    ),
  familyMembers: Yup.array()
    .of(
      Yup.lazy((value) => {
        if (
          !get(value, 'firstName', '') &&
          !get(value, 'lastName', '') &&
          !get(value, 'idNumber', '')
        ) {
          return Yup.mixed().notRequired();
        }
        return Yup.object()
          .shape({
            firstName: Yup.string().required('Friend or family member first name is required.'),
            lastName: Yup.string().required('Friend or family member last name is required.'),
            idNumber: baseOffenderValidationSchema.offenderIdentifierIndividualValue,
          })
          .test('uniqueFamilyIdNumber', '', uniqueIdentifierIn('idNumber'));
      }) as any,
    )
    .max(20, 'You can only add up to 20 family members.'),
  businessEntities: Yup.array()
    .of(
      Yup.lazy((businessEntity) => {
        if (
          isEmpty(businessEntity.entityName) &&
          businessEntity.links.every((link) => isEmpty(link.value))
        ) {
          return Yup.mixed().notRequired();
        }
        return Yup.object({
          entityName: Yup.string().required('Name of business entity is required'),
          links: baseLinksValidationSchema.links.test(
            'minimum',
            'Must Specify at least one ProxyID or BRN',
            linkArrayLengthOfOne,
          ),
        });
      }) as any,
    )
    .test('minimum', 'Must Specify at least one Business Entity', entityArrayLengthOfOne),
  offenderIdentifier: Yup.object().shape({
    value: baseOffenderValidationSchema.offenderIdentifierIndividualValue,
    identifierType: baseOffenderValidationSchema.offenderIdentifierIndividualType,
  }),
  offenderIdentifierFleet: Yup.object().shape({
    value: baseOffenderValidationSchema.offenderIdentifierFleetValue,
    identifierType: baseOffenderValidationSchema.offenderIdentifierFleetType,
  }),
  entityName: Yup.string().required('Entity Name is required'),
  address: Yup.string().notRequired(),
  vatNumber: Yup.string().notRequired(),
  companyRegistration: Yup.string().notRequired(),
};

export const loginValidation = Yup.object({
  username: validationSchema.email,
  password: validationSchema.password,
});

export const forgotPasswordValidation = Yup.object({
  email: validationSchema.email,
});

export const forgotPasswordOtpValidation = Yup.object({
  code: validationSchema.code,
  cellphone: validationSchema.cellphone,
});

export const OtpValidation = Yup.object({
  otp: validationSchema.otp,
  password: validationSchema.password,
  confirmPassword: validationSchema.confirmPassword,
});

export const ConfirmationOtpValidation = Yup.object({
  otp: validationSchema.otp,
});

export const resetPasswordValidation = Yup.object({
  password: validationSchema.password,
  confirmPassword: validationSchema.confirmPassword,
});

// A profile consists of possibly a individual and/or a fleet
// If it's only a fleet then the user doesn't have an individual
// profile and we cannot validate it then
export const editFleetOnlyAccountValidation = Yup.object({
  firstName: validationSchema.firstName,
  lastName: validationSchema.lastName,
  code: validationSchema.code,
  cellphoneNumber: validationSchema.cellphoneNumber,
  cellphone: validationSchema.cellphone,
  email: validationSchema.email,
  confirmEmail: validationSchema.confirmEmail,
  communicationEmail: validationSchema.communicationEmail,
});

export const editAccountValidation = Yup.object({
  firstName: validationSchema.firstName,
  lastName: validationSchema.lastName,
  code: validationSchema.code,
  cellphoneNumber: validationSchema.cellphoneNumber,
  cellphone: validationSchema.cellphone,
  email: validationSchema.email,
  confirmEmail: validationSchema.confirmEmail,
  idNumber: validationSchema.idNumber,
  communicationEmail: validationSchema.communicationEmail,
});

export const onboardIndividualValidation = Yup.object({
  idNumber: validationSchema.idNumber,
  confirmIdNumber: validationSchema.confirmIdNumber,
  familyMembers: validationSchema.familyMembers,
});

export const externalValidation = Yup.object().shape({
  account: Yup.object().shape({
    email: validationSchema.email,
  }),
  individual: Yup.object().shape({
    idNumber: validationSchema.idNumber,
  }),
});

export const editFamilyValidation = Yup.object({
  idNumber: validationSchema.idNumber,
  confirmIdNumber: validationSchema.confirmIdNumber,
  familyMembers: validationSchema.familyMembers,
});

export const onboardBusinessValidation = Yup.object({
  businessEntities: validationSchema.businessEntities,
});

export const addReferralValidation = Yup.object({
  referrals: Yup.array()
    .of(
      Yup.object({
        firstName: validationSchema.firstName,
        emailAddress: validationSchema.email,
      }),
    )
    .test('Unique', 'Referral emails must be unique', (values) => {
      return new Set(values.map((value) => value.emailAddress)).size === values.length;
    }),
});
const accountValidation = Yup.object({
  firstName: validationSchema.firstName,
  lastName: validationSchema.lastName,
  email: validationSchema.email,
  confirmEmail: validationSchema.confirmEmail,
  code: validationSchema.code,
  cellphoneNumber: validationSchema.cellphoneNumber,
  cellphone: validationSchema.cellphone,
  password: validationSchema.password,
  confirmPassword: validationSchema.confirmPassword,
  termsChecked: validationSchema.termsChecked,
  province: validationSchema.province,
});

export const signUpValidation = Yup.object({
  account: accountValidation,
  individual: onboardIndividualValidation,
  business: onboardBusinessValidation,
});

export const signUpValidationBusiness = Yup.object({
  account: accountValidation,
  business: onboardBusinessValidation,
});

export const signUpValidationIndividual = Yup.object({
  account: accountValidation,
  individual: onboardIndividualValidation,
});

export const adminFamilyMemberValidationSchema = Yup.object().shape({
  firstName: validationSchema.firstName,
  lastName: validationSchema.lastName,
  offenderIdentifier: validationSchema.offenderIdentifier,
});

export const fleetEntityFormValidationSchema = Yup.object().shape({
  entityName: validationSchema.entityName,
  address: validationSchema.address,
  vatNumber: validationSchema.vatNumber,
  companyRegistration: validationSchema.companyRegistration,
  links: baseLinksValidationSchema.links,
});

export const editFleetEntityLinkValidationSchema = validationSchema.offenderIdentifierFleet;

export const addFleetEntityValidationSchema = Yup.object().shape({
  entityName: validationSchema.entityName,
  address: Yup.string().required('Address is Required'),
});

export const profileEditValidationSchema = Yup.object().shape({
  communicationEmail: validationSchema.communicationEmail,
});

export const callOutcomeFormValidationSchema = Yup.object().shape({
  callDate: Yup.string().required(),
  agentFullName: Yup.string().required(),
  callOutcome: Yup.number().required(),
  durationInSeconds: Yup.number().required(),
  doNotContact: Yup.boolean().required(),
  callBack: Yup.boolean().required(),
  callBackDate: Yup.date().when('callBack', (callBack) =>
    callBack?.toString() === 'true'
      ? Yup.date().required('Call-back Date is Required')
      : Yup.date().optional(),
  ),
});

export const editCallCentreProfileFormValidationSchema = Yup.object().shape({
  userId: Yup.string().required(),
  firstName: validationSchema.firstName,
  lastName: validationSchema.lastName,
  email: validationSchema.email,
  partner: Yup.string().required('Partner is required'),
  cellphoneNumber: Yup.string()
    .test((cellphoneNumber) => {
      const intCode = `+${cellphoneNumber.substring(0, 2)}`;
      const cellNum = cellphoneNumber.substring(2, cellphoneNumber.length + 1);
      const internationalNumber = `${intCode} ${cellNum}`;
      let phoneError: string;
      let phoneNumber: PhoneNumber;
      try {
        phoneNumber = parsePhoneNumberWithError(internationalNumber);
        phoneError =
          intCode.length === 3 && cellNum.length === 9
            ? 'Success'
            : phoneNumber.isValid()
            ? 'Success'
            : 'Invalid';
      } catch (error) {
        if (error instanceof ParseError) {
          phoneError = error.message;
        } else {
          phoneError = error;
        }
      }

      if (phoneError === 'Success') {
        return true;
      }
      if (phoneError === 'Invalid') {
        return false;
      }
      return false;
    })
    .length(11)
    .required('Phone Number is required'),
});

export const editCallCenterAgentSchema = Yup.object().shape({
  extension: Yup.string(),
});

export const AddBlackListSchema = Yup.object().shape({
  municipalities: Yup.array()
    .of(Yup.string().required('Municipality name is required'))
    .min(1, 'At least one municipality must be added')
    .required('Municipalities is required'),
});

export const AddLeadPartnerSchema = Yup.object().shape({
  name: Yup.string().required('Lead Partner name is required'),
  email: Yup.string().email('Email must be a valid email'),
  utmSource: Yup.string().required('UTM Source is required'),
});
